import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTeamColors } from 'hooks';
import { api } from 'api/api';
import moment from 'moment';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import SEO from 'components/SEO/SEO';
import { Loading } from 'components/Loading/Loading';
import TopNav from 'components/TopNav';
import { detectInAppWebView } from 'utils/browserDetection';

import { StyledHeader } from 'components/App/style';
import {
  ArticleContainer,
  ArticleHeader,
  ArticleTitle,
  ArticleContent,
  GameLink,
  ErrorMessage,
} from './style';

export const NewsArticle = () => {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const { altColor } = useTeamColors();
  const [newsData, setNewsData] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [stadiumData, setStadiumData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processedContent, setProcessedContent] = useState('');

  // Check if we should show header (same logic as MainPage)
  const { isInAppWebView } = detectInAppWebView();
  const shouldShowHeader = !isInAppWebView; // This will be boolean, never undefined

  // Check if mobile for responsive padding
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // TopNav is fixed at 56px, so add extra padding below it
  const topPadding = shouldShowHeader ? (isMobile ? '75px' : '75px') : '20px';
  const containerHeight = shouldShowHeader
    ? `calc(100vh - ${isMobile ? '75px' : '75px'})`
    : '100vh';

  useEffect(() => {
    const fetchNewsArticle = async () => {
      try {
        setLoading(true);
        // Fetch all news and filter by news_id since API doesn't have single news endpoint
        const response = await api.news.getAllNews();

        if (response.data && response.data.length > 0) {
          // Find the specific news item by news_id
          const article = response.data.find(
            (item) => item.news_id === parseInt(newsId)
          );

          if (article) {
            // If this is a third-party URL, open in new tab
            if (article.news_url && !article.news_content) {
              window.open(article.news_url, '_blank');
              navigate(-1); // Go back after opening
              return;
            }

            // Otherwise, display the hosted content
            setNewsData(article);

            // Process content to remove title and citations
            if (article.news_content) {
              let content = article.news_content;

              // Remove exact title match from content
              if (article.news_title) {
                const titleRegex = new RegExp(
                  article.news_title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                  'gi'
                );
                content = content.replace(titleRegex, '');
              }

              // Remove citation patterns like [1], [2], [1][2], etc.
              content = content.replace(/\[\d+\]/g, '');

              setProcessedContent(content);
            } else {
              setProcessedContent('');
            }

            // Fetch event data if news_event_id exists
            if (article.news_event_id) {
              try {
                const eventResponse = await api.events.getEventById(
                  article.news_event_id
                );
                if (eventResponse.data) {
                  console.log(
                    'EVENT DATA FIELDS:',
                    Object.keys(eventResponse.data)
                  );
                  console.log('FULL EVENT DATA:', eventResponse.data);
                  setEventData(eventResponse.data);

                  // Fetch stadium data for timezone if event has location_id
                  if (eventResponse.data.event_location_id) {
                    try {
                      const stadiumResponse =
                        await api.games.getStadiumByLocationId(
                          eventResponse.data.event_location_id
                        );
                      if (stadiumResponse.data) {
                        setStadiumData(stadiumResponse.data);
                      }
                    } catch (stadiumErr) {
                      console.error('Error fetching stadium data:', stadiumErr);
                    }
                  }
                }
              } catch (eventErr) {
                console.error('Error fetching event data:', eventErr);
              }
            }
          } else {
            setError('Article not found');
          }
        } else {
          setError('No news available');
        }
      } catch (err) {
        console.error('Error fetching news article:', err);
        setError('Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    if (newsId) {
      fetchNewsArticle();
    }
  }, [newsId, navigate]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <>
        {shouldShowHeader === true ? (
          <StyledHeader>
            <TopNav />
          </StyledHeader>
        ) : null}
        <ArticleContainer
          style={{
            background: shouldShowHeader ? 'rgba(0, 0, 0, 0.9)' : '#000',
            paddingTop: topPadding,
            height: containerHeight,
            minHeight: containerHeight,
            maxHeight: containerHeight,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            scrollbarColor: '#666 #2a2a2a',
            boxSizing: 'border-box',
            padding: 0,
          }}
        >
          <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
            <ErrorMessage $mainColor={altColor}>{error}</ErrorMessage>
          </div>
        </ArticleContainer>
      </>
    );
  }

  if (!newsData) {
    return null;
  }

  // Strip citation markers from title
  const cleanTitle =
    newsData.news_title?.replace(/\[\d+\]/g, '') || newsData.news_title;

  // Extract team names from event data
  let gameInfo = null;
  if (newsData?.news_id && eventData) {
    // Get main team and opponent from event API response
    const homeTeam = eventData.event_main_team || '';
    const homeTeamId = eventData.event_main_team_id;
    const opponent = eventData.event_text || '';

    // Only show link if we have both teams and event data
    if (homeTeam && opponent && eventData) {
      // Determine home/away from event_location
      const isHomeGame =
        eventData.event_location === '0' || eventData.event_location === '2';
      const vsOrAt = isHomeGame ? 'vs' : 'at';

      // Format date/time
      const eventStart = eventData.event_start;
      const timezone = stadiumData?.stadium_time_zone || 'America/Los_Angeles';

      if (eventStart) {
        const gameTime = moment.utc(eventStart).tz(timezone);
        const formattedDate = gameTime.format('ddd, MMM D, YYYY');
        const formattedTime = gameTime.format('h:mm A z');

        gameInfo = {
          teamA: homeTeam,
          teamB: opponent,
          vsOrAt,
          formattedDate,
          formattedTime,
          eventId: eventData.event_id,
          league: eventData.event_league_name,
          leagueId: eventData.event_league_id,
          teamId: homeTeamId,
        };
      }
    }
  }

  return (
    <>
      <SEO
        description={newsData.news_content?.substring(0, 160)}
        title={cleanTitle}
      />
      {shouldShowHeader === true ? (
        <StyledHeader>
          <TopNav />
        </StyledHeader>
      ) : null}
      <ArticleContainer
        style={{
          background: shouldShowHeader ? 'rgba(0, 0, 0, 0.9)' : '#000',
          paddingTop: topPadding,
          height: containerHeight,
          minHeight: containerHeight,
          maxHeight: containerHeight,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin',
          scrollbarColor: '#666 #2a2a2a',
          boxSizing: 'border-box',
          padding: 0,
        }}
      >
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
          <ArticleHeader $borderColor={altColor}>
            <ArticleTitle>{cleanTitle}</ArticleTitle>
            {gameInfo ? (
              <GameLink
                $color={altColor}
                href={`/?league=${gameInfo.league}&leagueId=${gameInfo.leagueId}&team=${encodeURIComponent(gameInfo.teamA)}&teamId=${gameInfo.teamId}&eventId=${gameInfo.eventId}`}
                onClick={(e) => {
                  e.preventDefault();
                  window.open(
                    `/?league=${gameInfo.league}&leagueId=${gameInfo.leagueId}&team=${encodeURIComponent(gameInfo.teamA)}&teamId=${gameInfo.teamId}&eventId=${gameInfo.eventId}`,
                    '_blank'
                  );
                }}
                rel="noopener noreferrer"
                target="_blank"
              >
                {gameInfo.teamA} {gameInfo.vsOrAt} {gameInfo.teamB}
                {gameInfo.formattedDate && gameInfo.formattedTime ? (
                  <>
                    {' '}
                    on {gameInfo.formattedDate} at {gameInfo.formattedTime}
                  </>
                ) : null}
              </GameLink>
            ) : null}
          </ArticleHeader>

          <ArticleContent>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {processedContent}
            </ReactMarkdown>
          </ArticleContent>
        </div>
      </ArticleContainer>
    </>
  );
};
